import React, { useCallback, useState } from 'react';
import {
  bool, func, string, shape, arrayOf
} from 'prop-types';

import { extend } from '@thd-nucleus/data-sources';

import { noop } from '../../utils/promo-utils';
import { usePromoModel } from '../../contexts/PromoModelProvider';
import { PromoSidecar } from '../promo-sidecar/PromoSidecar';
import { PromoQuickviewDetails } from './subcomponents/promo-quickview-details/PromoQuickviewDetails';
import { PromoQuickviewOverview } from './subcomponents/promo-quickview-overview/PromoQuickviewOverview';
import { PromoQuickviewSpecs } from './subcomponents/promo-quickview-specs/PromoQuickviewSpecs';
import { PromoQuickviewReviews } from './subcomponents/promo-quickview-reviews/PromoQuickviewReviews';
import './PromoQuickview.scss';

export const PromoQuickview = ({
  isQuickviewOpen,
  setIsQuickviewOpen,
  product,
  storeId,
  selected,
  onSelect,
  isInPromoList,
  setQuantityWarningText,
  showQuantityWarningState,
  displayPosition
}) => {
  const { analyticsComponent } = usePromoModel();

  const [sectionClicked, setSectionClicked] = useState({
    'product overview': false,
    specifications: false,
    reviews: false,
  });

  const itemId = product.itemId;
  const images = Array.isArray(product.media?.images) ? product.media.images : [];
  const brand = product.identifiers.brandName || '';
  const description = product.identifiers.productLabel || '';
  const overview = product.details?.description || '';
  const highlights = Array.isArray(product.details?.descriptiveAttributes)
    ? product.details.descriptiveAttributes : [];
  const averageRating = Number(product.reviews?.ratingsReviews?.averageRating) || 0;
  const totalRatings = product.reviews?.ratingsReviews?.totalReviews || '0';

  const onSectionClick = useCallback((section) => {
    if (!sectionClicked[section]) {
      setSectionClicked({ ...sectionClicked, [section]: true });

      window.LIFE_CYCLE_EVENT_BUS.trigger('promotion-products-quickview-section.click', {
        component: analyticsComponent,
        section: 'quick view',
        target: section,
      });
    }
  }, [sectionClicked, analyticsComponent]);

  return (
    <PromoSidecar
      isSidecarOpen={isQuickviewOpen}
      setIsSidecarOpen={setIsQuickviewOpen}
      zIndex={1000}
      itemId={itemId}
      useSidecarNav
      useSidecarCta
      useSidecarMask={!isInPromoList}
      selected={selected}
      onSelect={onSelect}
      setQuantityWarningText={setQuantityWarningText}
      showQuantityWarningState={showQuantityWarningState}
      displayPosition={displayPosition}
    >
      <article
        className="promo-quickview"
        data-testid={`promo-quickview${isQuickviewOpen ? '--open' : '--closed'}`}
      >
        {brand && description && (
          <>
            <PromoQuickviewDetails
              storeId={storeId}
              itemId={itemId}
              brand={brand}
              images={images}
              description={description}
            />
            <section className="promo-quickview-sections-container">
              <PromoQuickviewOverview
                itemId={itemId}
                overview={overview}
                highlights={highlights}
                onSectionClick={onSectionClick}
                selected={selected}
                onSelect={onSelect}
                setQuantityWarningText={setQuantityWarningText}
                showQuantityWarningState={showQuantityWarningState}
              />
              <PromoQuickviewSpecs
                itemId={itemId}
                storeId={storeId}
                onSectionClick={onSectionClick}
                selected={selected}
                onSelect={onSelect}
                setQuantityWarningText={setQuantityWarningText}
                showQuantityWarningState={showQuantityWarningState}
              />
              <PromoQuickviewReviews
                itemId={itemId}
                averageRating={averageRating}
                totalRatings={totalRatings}
                onSectionClick={onSectionClick}
                selected={selected}
                onSelect={onSelect}
                setQuantityWarningText={setQuantityWarningText}
                showQuantityWarningState={showQuantityWarningState}
              />
            </section>
          </>
        )}
      </article>
    </PromoSidecar>
  );
};

PromoQuickview.displayName = 'PromoQuickview';

PromoQuickview.defaultProps = {
  isInPromoList: true,
  setQuantityWarningText: noop,
  showQuantityWarningState: false
};

PromoQuickview.propTypes = {
  selected: bool.isRequired,
  onSelect: func.isRequired,
  storeId: string.isRequired,
  isQuickviewOpen: bool.isRequired,
  setIsQuickviewOpen: func.isRequired,
  product: shape({
    itemId: string,
    media: shape({
      image: shape({
        url: string,
        type: string,
      }),
      images: arrayOf(shape({
        url: string,
        type: string,
      })),
    }),
    identifiers: shape({
      brand: string,
      description: string,
    }),
    details: shape({
      overview: string,
      highlights: arrayOf(string),
    }),
    reviews: shape({
      ratingsReviews: shape({
        averageRating: string,
        totalReviews: string,
      }),
    }),
  }).isRequired,
  isInPromoList: bool,
  setQuantityWarningText: func,
  showQuantityWarningState: bool,
  displayPosition: string.isRequired
};

PromoQuickview.dataModel = extend({}, PromoQuickviewSpecs);